import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchHomePopup } from '../lib/api.js';
import { videoEmbed } from '../lib/videoEmbed.js';

const DISMISS_KEY = 'mlis_popup_dismissed';

/**
 * Homepage popup/modal. Content (text / image+text / video / text+video) is
 * fully admin-managed via the Popup resource; the public endpoint returns null
 * when the admin has it disabled, so this renders nothing in that case.
 *
 * It appears shortly after load, can be dismissed, and stays dismissed until
 * the admin next edits the popup (dismissal is keyed by the row's updatedAt,
 * so updated content shows again).
 */
export function HomePopup() {
  const { data: popup } = useApi(fetchHomePopup, []);
  const [open, setOpen] = useState(false);

  const close = () => {
    setOpen(false);
    try { if (popup) localStorage.setItem(DISMISS_KEY, String(popup.updatedAt || '')); } catch { /* ignore */ }
  };

  // Open shortly after the popup loads, unless this exact version was dismissed.
  useEffect(() => {
    if (!popup) return undefined;
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === String(popup.updatedAt || ''); } catch { /* ignore */ }
    if (dismissed) return undefined;
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [popup]);

  // While open: lock body scroll + close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!popup || !open) return null;

  const showText = popup.type !== 'video';
  const showImage = popup.type === 'image_text' && !!popup.image;
  const wantsVideo = popup.type === 'video' || popup.type === 'video_text';
  const vid = wantsVideo && popup.videoUrl ? videoEmbed(popup.videoUrl) : null;
  const ctaIsExternal = popup.ctaUrl && /^https?:\/\//i.test(popup.ctaUrl);

  return (
    <div className="popup-overlay" onClick={close} role="presentation">
      <div
        className="popup-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={popup.title || 'Announcement'}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="popup-close" onClick={close} aria-label="Close">×</button>

        {showImage && (
          <div className="popup-media">
            <img src={popup.image} alt={popup.title || ''} />
          </div>
        )}

        {vid && (
          <div className="popup-media popup-video">
            {vid.kind === 'iframe' ? (
              <iframe
                src={vid.src}
                title={popup.title || 'Video'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={vid.src} controls />
            )}
          </div>
        )}

        <div className="popup-body">
          {popup.title && <h2 className="popup-title">{popup.title}</h2>}
          {showText && popup.body && (
            <div className="popup-text" dangerouslySetInnerHTML={{ __html: popup.body }} />
          )}
          {popup.ctaLabel && popup.ctaUrl && (
            ctaIsExternal ? (
              <a className="btn btn-green popup-cta" href={popup.ctaUrl} target="_blank" rel="noopener noreferrer" onClick={close}>
                {popup.ctaLabel}
              </a>
            ) : (
              <Link className="btn btn-green popup-cta" to={popup.ctaUrl} onClick={close}>
                {popup.ctaLabel}
              </Link>
            )
          )}
        </div>
      </div>
    </div>
  );
}
